<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <v-card>
      <v-card-title>
        {{ $t('vehicle.transfer-vehicle') }}
      </v-card-title>

      <v-card-text>
        <!-- État courant : l'assignation est double (organisation + membre). -->
        <div class="current-assignment mb-4">
          <div class="current-assignment__title">{{ $t('vehicle.current-assignment') }}</div>
          <div class="current-assignment__row">
            <v-icon x-small>mdi-domain</v-icon>
            <span class="ca-lbl">{{ $t('organization.organization') }}</span>
            <span class="ca-val">{{ currentOrgLabel }}</span>
          </div>
          <div class="current-assignment__row">
            <v-icon x-small>mdi-account-outline</v-icon>
            <span class="ca-lbl">{{ $tc('member.member') }}</span>
            <span class="ca-val" :class="{ 'ca-val--empty': !vehicleHasCustomer }">{{ currentMemberLabel }}</span>
          </div>
        </div>

        <v-radio-group v-model="transferMode" class="mt-2" hide-details>
          <v-radio value="organization" :label="$t('vehicle.transfer-to-organization')" />
          <v-radio value="member" :label="$t('vehicle.transfer-to-member')" />
          <v-radio
              v-if="vehicleHasCustomer"
              value="unassign"
              :label="$t('vehicle.unassign-member')"
          />
        </v-radio-group>

        <template v-if="transferMode === 'organization'">
          <organization-select
              v-model="targetOrganizationId"
              :label="$t('organization.organization')"
              clearable
              outlined
              dense
          />
          <v-alert v-if="isSameOrganization" type="error" text dense class="mt-2">
            {{ $t('vehicle.transfer-same-organization') }}
          </v-alert>
        </template>

        <template v-if="transferMode === 'member'">
          <member-select
              v-model="targetMemberId"
              :label="$tc('member.member')"
              :organization-id="organizationId"
              clearable
          />
        </template>

        <v-alert v-if="modeHint" type="info" text dense class="mt-3 mb-0">
          {{ modeHint }}
        </v-alert>

        <v-text-field
            :label="$t('vehicle.reason') + ' (' + $t('optional') + ')'"
            v-model="reason"
            outlined
            hide-details
            class="mt-4"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn small @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn small color="primary" @click="confirm" :disabled="!isValid">
          {{ $t('vehicle.confirm-transfer') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository";
import OrganizationSelect from "@blocks/Select/OrganizationSelect";
import MemberSelect from "@blocks/Select/MemberSelect";

export default {
  components: { OrganizationSelect, MemberSelect },

  props: {
    value: Boolean,
    vehicle: Object,
    organizationId: String,
  },

  data() {
    return {
      transferMode: 'organization',
      targetOrganizationId: null,
      targetMemberId: null,
      reason: null,
    }
  },

  computed: {
    dialog: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },

    vehicleHasCustomer() {
      return this.vehicle && !!this.vehicle.customer
    },

    currentOrgLabel() {
      const org = this.vehicle && this.vehicle.organization
      if (!org) return '—'
      const code = org.code || org.zmsCode
      return code ? `${org.name} (${code})` : org.name
    },

    currentMemberLabel() {
      const c = this.vehicle && this.vehicle.customer
      if (!c) return this.$t('vehicle.no-member')
      // Name, else the EVCO ID (name is optional), else the no-member label.
      return `${c.firstname || ''} ${c.lastname || ''}`.trim() || c.evcoId || this.$t('vehicle.no-member')
    },

    // Message clair de ce que fera le transfert selon le mode (l'assignation est double :
    // organisation + membre), au lieu d'un avertissement générique trompeur.
    modeHint() {
      if (this.transferMode === 'organization') return this.$t('vehicle.transfer-hint-organization')
      if (this.transferMode === 'member') return this.$t('vehicle.transfer-hint-member')
      if (this.transferMode === 'unassign') return this.$t('vehicle.transfer-hint-unassign')
      return ''
    },

    isSameOrganization() {
      return this.transferMode === 'organization' && this.targetOrganizationId === this.organizationId
    },

    isValid() {
      if (this.transferMode === 'organization') return !!this.targetOrganizationId && !this.isSameOrganization
      if (this.transferMode === 'member') return !!this.targetMemberId
      if (this.transferMode === 'unassign') return true
      return false
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.transferMode = 'organization'
        this.targetOrganizationId = null
        this.targetMemberId = null
        this.reason = null
      }
    },
  },

  methods: {
    close() {
      this.dialog = false
    },

    confirm() {
      if (!this.vehicle) return

      if (this.transferMode === 'unassign') {
        this.doUnassign()
      } else {
        this.doTransfer()
      }
    },

    doTransfer(force = false) {
      let data = {}

      if (this.transferMode === 'organization') {
        data = {
          fromType: 'organization',
          toType: 'organization',
          toId: this.targetOrganizationId,
        }
      } else if (this.transferMode === 'member') {
        data = {
          fromType: 'member',
          toType: 'member',
          toId: this.targetMemberId,
        }
      }

      if (this.reason) data.reason = this.reason
      if (force) data.force = true

      this.showLoading(true)
      VehicleRepository.transfer(this.vehicle.id, data).then(() => {
        this.showLoading(false)
        this.$emit('vehicle-transferred')
        this.close()
      }).catch(err => {
        this.showLoading(false)
        // Conflit d'attribution : récupération (autre utilisateur) ou forçage opérateur (autre org).
        const action = this.vehicleConflictAction(err)
        if (!force && action) {
          this.$dialog.confirm({ text: this.$t(action.confirmKey) }).then(ok => {
            if (ok) this.doTransfer(true)
          })
          return
        }
        this.notifyError(err)
      })
    },

    doUnassign() {
      const data = this.reason ? { reason: this.reason } : {}

      this.showLoading(true)
      VehicleRepository.unassignCustomer(this.vehicle.id, data).then(() => {
        this.showLoading(false)
        this.$emit('vehicle-transferred')
        this.close()
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-card__text {
  overflow: visible;
}
.current-assignment {
  background: #f5f7f8;
  border: 1px solid #eceff1;
  border-radius: 8px;
  padding: 12px 14px;
  &__title {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
    color: #607d8b; margin-bottom: 8px;
  }
  &__row {
    display: flex; align-items: center; gap: 8px; font-size: 13.5px; padding: 2px 0;
    .ca-lbl { color: #90a4ae; min-width: 110px; }
    .ca-val { color: #37474f; font-weight: 500; }
    .ca-val--empty { color: #b0bec5; font-style: italic; font-weight: 400; }
  }
}
</style>
